import React, { useEffect, useState } from 'react';
import { MAX_PAX, MAX_PAX_SELLER } from 'Utils/faceting/constants';
import type { ValidationState } from 'Store/trip-information/kids/constants';
import 'Store/trip-information/kids/constants';
import { isKidMinor } from 'Utils/faceting/facetingUtils';
import { hasKidWithoutAValidBirthdate } from 'Utils/faceting/kidsValidationStateUtils';
import { useAppContext } from 'Context/ReactAppContext';
import { FormFeedback } from 'Components/frameworks/form-feedback/FormFeedback';
import { InputDate } from 'Components/frameworks/form-inputs/input-date/InputDate';
import { InputNumber } from 'Components/frameworks/form-inputs/input-number/InputNumber';
import { InputSwitch } from 'Components/frameworks/form-inputs/input-switch/InputSwitch';
type Props = {
  addChildcareLabel: string;
  adultsLabel: string;
  adultsNumber: number;
  birthdaysErrorMessage?: string;
  changeKid: (arg0: number, arg1: Kid) => void;
  changeKidsNumber: (arg0: number) => void;
  dateFormat: string;
  dayPlaceholder: string;
  disableKids?: boolean;
  hasChildcareSwitch?: boolean;
  informationLabel?: string;
  isShrink?: boolean;
  kids: ReadonlyArray<any>;
  kidsAgeLabel: string;
  kidsAgeLimitLabel: string;
  kidsLabel: string;
  kidsValidationState: Array<ValidationState>;
  label: string;
  minAdultsNumber?: number;
  monthPlaceholder: string;
  setAdultsNumber: (arg0: number) => void;
  validateKidsBirthdate: (arg0: number, arg1: Kid) => void;
  yearPlaceholder: string;
};
export const FilterWho = ({
  addChildcareLabel,
  adultsLabel,
  adultsNumber,
  birthdaysErrorMessage = '',
  changeKidsNumber,
  changeKid,
  dateFormat,
  dayPlaceholder,
  disableKids,
  hasChildcareSwitch,
  informationLabel = '',
  isShrink,
  kids,
  kidsAgeLabel,
  kidsAgeLimitLabel,
  kidsLabel,
  kidsValidationState,
  label,
  minAdultsNumber = 1,
  monthPlaceholder,
  setAdultsNumber,
  validateKidsBirthdate,
  yearPlaceholder,
}: Props) => {
  const appContext = useAppContext();
  const [childcareIncluded, setChildcareIncluded] = useState(
    kids.some((kid) => kid.childcareIncluded),
  );

  const handleKidsNumber = (_name: string, value: number): void => {
    if (changeKidsNumber) {
      changeKidsNumber(value);
    }
  };

  const handleKidAge = async (index: number, kid: any) => {
    // check date validity
    if (kid.birthdate) {
      await validateKidsBirthdate(index, kid);

      if (isKidMinor(kid.birthdate)) {
        changeKid(index, kid);
      }
    }
  };

  const maxPax = appContext.isSeller ? MAX_PAX_SELLER : MAX_PAX;

  const getMaxAdultsAvailable = () => {
    return maxPax - kids.length;
  };

  const getMaxKidsAvailable = () => {
    return maxPax - adultsNumber;
  };

  useEffect(() => {
    hasChildcareSwitch &&
      kids.forEach((kid, index) => changeKid(index, { ...kid, childcareIncluded }));
  }, [childcareIncluded]);
  return (
    <div data-testid="FilterWho" className="FilterWho">
      <div className="p-20">
        <p className="mb-20 text-18 font-bold lg:text-center">{label}</p>
        <div className="flex flex-col gap-20 lg:flex-row lg:items-center">
          <InputNumber
            ariaLabel={adultsLabel}
            dataTestId="FilterWho-Adults"
            hasColumnLayout={isShrink}
            label={adultsLabel}
            max={getMaxAdultsAvailable()}
            min={minAdultsNumber}
            onChange={(_name, value) => {
              setAdultsNumber(value || 0);
            }}
            value={adultsNumber}
          />
          {!disableKids && (
            <InputNumber
              ariaLabel={kidsLabel}
              dataTestId="FilterWho-Kids"
              hasColumnLayout={isShrink}
              label={kidsLabel}
              max={getMaxKidsAvailable()}
              notes={kidsAgeLimitLabel}
              onChange={handleKidsNumber}
              value={kids.length}
            />
          )}
        </div>
      </div>
      {!disableKids && kids.length > 0 && hasChildcareSwitch && (
        <div className="border-t-1 border-lightGrey p-10">
          <InputSwitch
            inputTestId="childcare-switch"
            isChecked={childcareIncluded}
            label={addChildcareLabel}
            onChange={(_name, isChecked) => setChildcareIncluded(isChecked)}
            value={childcareIncluded}
          />
        </div>
      )}

      {!disableKids && kids.length > 0 && (
        <div className="border-t-1 border-lightGrey p-10">
          <div className="mb-10">
            <p className="text-14 font-bold text-sienna">{kidsAgeLabel}</p>
            {informationLabel && <p className="text-10">{informationLabel}</p>}
          </div>
          {hasKidWithoutAValidBirthdate(kidsValidationState) && birthdaysErrorMessage && (
            <FormFeedback isError message={birthdaysErrorMessage} />
          )}
          <div className={!isShrink ? 'lg:-mx-10 lg:flex lg:flex-wrap' : null}>
            {kids.map((kid, index) => (
              <div
                className={!isShrink ? 'w-full flex-none lg:w-1/2 lg:px-10' : null}
                key={String(kid.birthdate) + String(index)}
              >
                <InputDate
                  className="mb-10"
                  dateFormat={dateFormat}
                  onChange={(_name, birthdate) => handleKidAge(index, { ...kid, birthdate })}
                  placeholderDay={dayPlaceholder}
                  placeholderMonth={monthPlaceholder}
                  placeholderYear={yearPlaceholder}
                  validationState={kidsValidationState[index]}
                  value={kid.birthdate}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
